/**
 * BookingWidgetDemo - Standalone demo page for the Quick Booking Widget
 * 
 * This page simulates how the widget would appear on a funnel Thank You page.
 * It accepts contact information via URL parameters for testing.
 * 
 * Usage:
 * /booking-demo?first_name=John&last_name=Doe&email=john@example.com&phone=5551234567
 */

import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QuickBookingWidget } from '@/components/booking';

export default function BookingWidgetDemo() {
  const [searchParams] = useSearchParams();
  
  // Extract contact info from URL params (simulating funnel submission data)
  const contact = useMemo(() => ({
    first_name: searchParams.get('first_name') || 'Test',
    last_name: searchParams.get('last_name') || 'User',
    email: searchParams.get('email') || 'test@example.com',
    phone: searchParams.get('phone') || '5551234567',
    contact_id: searchParams.get('contact_id') || null,
  }), [searchParams]);
  
  // Configuration can also be passed via URL params
  const calendarId = searchParams.get('calendar_id') || 'FrDjJmt2K0IuNvgDJJIE';
  const locationId = searchParams.get('location_id') || 'waa4Ckpna9DBcLA3LBEA';
  
  const handleBookingSuccess = (result) => {
    console.log('[BookingDemo] Booking successful:', result);
  };
  
  const handleBookingError = (error) => {
    console.error('[BookingDemo] Booking error:', error);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 py-8 px-4">
      {/* Demo header */}
      <div className="max-w-md mx-auto mb-6 text-center">
        <div className="inline-block bg-amber-100 text-amber-800 text-xs font-medium px-3 py-1 rounded-full mb-4">
          Demo Mode
        </div>
        <h1 className="text-2xl font-bold text-slate-800 mb-2">
          Quick Booking Widget Demo
        </h1>
        <p className="text-slate-600 text-sm">
          This is a preview of how the widget appears on funnel Thank You pages.
        </p>
      </div>
      
      {/* Contact info preview */}
      <div className="max-w-md mx-auto mb-6 bg-white/50 backdrop-blur rounded-xl p-4 text-sm">
        <p className="text-slate-500 font-medium mb-2">Test Contact:</p>
        <p className="text-slate-700">{contact.first_name} {contact.last_name}</p>
        <p className="text-slate-600">{contact.email}</p>
        <p className="text-slate-600">{contact.phone}</p>
      </div>
      
      {/* The actual widget */}
      <QuickBookingWidget
        contact={contact}
        calendarId={calendarId}
        locationId={locationId}
        onBookingSuccess={handleBookingSuccess}
        onBookingError={handleBookingError}
      />
      
      {/* Demo footer with usage instructions */}
      <div className="max-w-md mx-auto mt-8 bg-slate-800 text-white rounded-xl p-4 text-sm">
        <p className="font-medium mb-2">URL Parameters:</p>
        <code className="block bg-slate-900 rounded p-2 text-xs overflow-x-auto">
          ?first_name=John&last_name=Doe&email=john@example.com&phone=5551234567
        </code>
      </div>
    </div>
  );
}
